import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Textarea } from './ui/textarea';
import {
  CheckCircle2, XCircle, PartyPopper, AlertTriangle, Heart, Phone, Camera,
  ShieldCheck, PenTool, Cake, Baby, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { ALLERGY_OPTIONS, MEDICAL_OPTIONS } from './AllergyFormCompact';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const toSet = (arr) => new Set(arr || []);

const Section = ({ icon: Icon, title, accent, children, testid }) => (
  <div className="bg-white rounded-2xl border border-[#E8E5DF] p-5 space-y-3 text-left" data-testid={testid}>
    <div className="flex items-center gap-2">
      <Icon className="w-4 h-4" style={{ color: accent }} />
      <h3 className="font-semibold text-sm text-[#1E1E24]">{title}</h3>
    </div>
    {children}
  </div>
);

/**
 * Unified guest flow shown on the invitation link (/rsvp/:token):
 * RSVP → guest child → birthday child (read-only) → allergies → emergency
 * contact → 3 consents → liability waiver (read-before-accept) → one Submit.
 */
export const UnifiedRsvpForm = ({
  token, guestName = '', birthdayChild = '', accent = '#FF3B6B',
  existingRsvp = null, existingForm = null, allergyCompleted = false, onDone,
}) => {
  const alreadyDone = existingRsvp === 'no' || (existingRsvp === 'yes' && allergyCompleted);
  const [response, setResponse] = useState(existingRsvp);         // 'yes' | 'no' | null
  const [editing, setEditing] = useState(!alreadyDone);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [waiverText, setWaiverText] = useState({ title: 'Liability Waiver', body: '' });
  const [guestChild, setGuestChild] = useState(existingForm?.attendee_name || guestName || '');
  const [allergies, setAllergies] = useState(() => toSet(existingForm?.medical_data?.allergies));
  const [allergyOther, setAllergyOther] = useState(existingForm?.medical_data?.allergy_other || '');
  const [medical, setMedical] = useState(() => toSet(existingForm?.medical_data?.medical_conditions));
  const [medicalOther, setMedicalOther] = useState(existingForm?.medical_data?.medical_other || '');
  const [emName, setEmName] = useState(existingForm?.emergency_contact_name || '');
  const [emPhone, setEmPhone] = useState(existingForm?.emergency_contact_phone || '');
  const [emRel, setEmRel] = useState(existingForm?.emergency_contact_relationship || '');
  const [emergencyTreatment, setEmergencyTreatment] = useState(existingForm?.emergency_treatment_consent ?? false);
  const [photoConsent, setPhotoConsent] = useState(existingForm?.photo_video_consent ?? true);
  const [waiverAccepted, setWaiverAccepted] = useState(existingForm?.liability_waiver_accepted ?? false);
  const [signature, setSignature] = useState(existingForm?.signature_name || '');

  const loadWaiver = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/waiver/text`);
      setWaiverText({ title: data.title, body: data.body, version: data.version });
    } catch (_) { /* keep default */ }
  }, []);
  useEffect(() => { loadWaiver(); }, [loadWaiver]);

  const toggle = (setter, set) => (id, checked) => {
    const next = new Set(set);
    if (checked) next.add(id); else next.delete(id);
    setter(next);
  };

  const canSubmitYes = guestChild.trim() && emName.trim() && emPhone.trim()
    && emergencyTreatment && waiverAccepted && signature.trim();

  const submit = async () => {
    if (!response) { toast.error('Please choose Yes or No first'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/public/rsvp/${token}`, { response, message });
      if (response === 'yes') {
        if (!canSubmitYes) {
          toast.error('Please complete the consent, waiver and signature to attend.');
          setSubmitting(false);
          return;
        }
        await axios.post(`${API_URL}/api/public/rsvp/${token}/allergy-form`, {
          attendee_name: guestChild.trim(),
          allergies: Array.from(allergies),
          allergy_other: allergyOther,
          medical_conditions: Array.from(medical),
          medical_other: medicalOther,
          emergency_contact_name: emName.trim(),
          emergency_contact_phone: emPhone.trim(),
          emergency_contact_relationship: emRel.trim(),
          photo_video_consent: !!photoConsent,
          emergency_treatment_consent: !!emergencyTreatment,
          liability_waiver_accepted: !!waiverAccepted,
          signature_name: signature.trim(),
          signature_date: new Date().toISOString().slice(0, 10),
          waiver_text_version: waiverText.version || 1,
        });
      }
      toast.success(response === 'yes' ? 'All set — thank you!' : 'Thanks for letting us know.');
      setEditing(false);
      onDone?.(response);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not submit — please try again');
    } finally { setSubmitting(false); }
  };

  // ── completed summary ──
  if (!editing) {
    return (
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="mt-7 p-5 rounded-2xl border-2 border-dashed" style={{ borderColor: accent }} data-testid="rsvp-submitted">
        <PartyPopper className="w-7 h-7 mx-auto mb-1" style={{ color: accent }} />
        <p className="font-semibold text-[#1E1E24]">
          {response === 'yes' ? "Awesome — we can't wait to see you!" : "Thanks for letting us know."}
        </p>
        <p className="text-xs text-[#6B6B7A] mt-1">
          Your response: <strong>{response === 'yes' ? "YES, I'll be there" : "No, I can't make it"}</strong>
          {response === 'yes' && allergyCompleted ? ' · forms complete' : ''}
        </p>
        <Button variant="outline" onClick={() => setEditing(true)} className="rounded-full mt-4 h-9" data-testid="rsvp-edit">
          View / edit my response
        </Button>
      </motion.div>
    );
  }

  return (
    <div className="mt-7 text-left" data-testid="unified-rsvp-form">
      {/* RSVP choice */}
      <div className="text-center">
        <p className="text-sm font-semibold text-[#1E1E24] mb-3">
          {guestName && guestName !== 'Guest' ? `${guestName}, will you join us?` : 'Will you attend?'}
        </p>
        <div className="flex flex-col sm:flex-row gap-2 justify-center">
          <Button onClick={() => setResponse('yes')} className="rounded-full text-white h-12 px-7 font-semibold"
            style={{ background: response === 'yes' ? accent : '#C9C7C0' }} data-testid="rsvp-yes">
            <CheckCircle2 className="w-4 h-4 mr-1.5" /> Yes, I'll be there!
          </Button>
          <Button onClick={() => setResponse('no')} variant={response === 'no' ? 'default' : 'outline'}
            className={`rounded-full h-12 px-7 font-semibold ${response === 'no' ? 'bg-[#1E1E24] text-white hover:bg-black' : ''}`} data-testid="rsvp-no">
            <XCircle className="w-4 h-4 mr-1.5" /> Sorry, can't make it
          </Button>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {response === 'no' && (
          <motion.div key="no" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
            <div className="mt-5">
              <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={2}
                placeholder="Add a note for the host (optional)…" className="text-sm rounded-xl" data-testid="rsvp-message" />
            </div>
          </motion.div>
        )}

        {response === 'yes' && (
          <motion.div key="yes" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="mt-6 space-y-4">
            {/* Guest + birthday child */}
            <Section icon={Baby} title="Who's attending?" accent={accent} testid="section-children">
              <div>
                <Label className="text-xs text-[#6B6B7A]">Guest child's full name *</Label>
                <Input value={guestChild} onChange={(e) => setGuestChild(e.target.value)} placeholder="The child attending"
                  className="rounded-xl border-[#E8E5DF] mt-1" data-testid="guest-child-name" />
              </div>
              <div className="mt-2">
                <Label className="text-xs text-[#6B6B7A] flex items-center gap-1"><Cake className="w-3 h-3" /> Birthday child (the party)</Label>
                <Input value={birthdayChild} readOnly disabled className="rounded-xl border-[#E8E5DF] mt-1 bg-[#F6F5F1] text-[#6B6B7A]" data-testid="birthday-child-name" />
              </div>
            </Section>

            {/* Allergies */}
            <Section icon={AlertTriangle} title="Allergies" accent={accent} testid="section-allergies">
              <div className="grid grid-cols-2 gap-2">
                {ALLERGY_OPTIONS.map((a) => (
                  <label key={a.id} className="flex items-center gap-2 text-sm">
                    <Checkbox checked={allergies.has(a.id)} onCheckedChange={(c) => toggle(setAllergies, allergies)(a.id, c)} data-testid={`u-allergy-${a.id}`} />
                    {a.label}
                  </label>
                ))}
              </div>
              {allergies.has('other') && (
                <Textarea value={allergyOther} onChange={(e) => setAllergyOther(e.target.value)} rows={2}
                  placeholder="Please specify other allergies" className="rounded-xl border-[#E8E5DF]" data-testid="u-allergy-other" />
              )}
            </Section>

            {/* Medical */}
            <Section icon={Heart} title="Medical conditions" accent={accent} testid="section-medical">
              <div className="grid grid-cols-2 gap-2">
                {MEDICAL_OPTIONS.map((m) => (
                  <label key={m.id} className="flex items-center gap-2 text-sm">
                    <Checkbox checked={medical.has(m.id)} onCheckedChange={(c) => toggle(setMedical, medical)(m.id, c)} data-testid={`u-medical-${m.id}`} />
                    {m.label}
                  </label>
                ))}
              </div>
              <Textarea value={medicalOther} onChange={(e) => setMedicalOther(e.target.value)} rows={2}
                placeholder="Any other conditions or notes (optional)" className="rounded-xl border-[#E8E5DF]" data-testid="u-medical-other" />
            </Section>

            {/* Emergency contact */}
            <Section icon={Phone} title="Emergency contact" accent={accent} testid="section-emergency">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div><Label className="text-xs">Name *</Label>
                  <Input value={emName} onChange={(e) => setEmName(e.target.value)} className="rounded-xl border-[#E8E5DF] mt-1" data-testid="u-emergency-name" /></div>
                <div><Label className="text-xs">Phone *</Label>
                  <Input value={emPhone} onChange={(e) => setEmPhone(e.target.value)} className="rounded-xl border-[#E8E5DF] mt-1" data-testid="u-emergency-phone" /></div>
                <div><Label className="text-xs">Relationship</Label>
                  <Input value={emRel} onChange={(e) => setEmRel(e.target.value)} placeholder="e.g. Parent" className="rounded-xl border-[#E8E5DF] mt-1" data-testid="u-emergency-rel" /></div>
              </div>
            </Section>

            {/* Consents */}
            <Section icon={Camera} title="Consent" accent={accent} testid="section-consent">
              <label className="flex items-start gap-2 text-sm">
                <Checkbox checked={emergencyTreatment} onCheckedChange={(c) => setEmergencyTreatment(!!c)} className="mt-0.5" data-testid="u-consent-emergency" />
                <span><strong>Emergency medical treatment (required):</strong> I authorize Creative Birthdays staff to administer basic first aid and to seek emergency medical treatment for my child if needed. *</span>
              </label>
              <label className="flex items-start gap-2 text-sm">
                <Checkbox checked={photoConsent} onCheckedChange={(c) => setPhotoConsent(!!c)} className="mt-0.5" data-testid="u-consent-photo" />
                <span><strong>Photo / video:</strong> I consent to photos and videos of my child being taken during the party and used for promotional purposes.</span>
              </label>
            </Section>

            {/* Liability waiver — read before accept */}
            <Section icon={ShieldCheck} title={waiverText.title || 'Liability Waiver'} accent={accent} testid="section-waiver">
              <div className="bg-[#FBFAF7] border border-[#E8E5DF] rounded-xl p-3 max-h-48 overflow-y-auto text-xs leading-relaxed text-[#4B4B55] whitespace-pre-line" data-testid="u-waiver-text">
                {waiverText.body || 'Loading waiver…'}
              </div>
              <label className="flex items-start gap-2 text-sm mt-2">
                <Checkbox checked={waiverAccepted} onCheckedChange={(c) => setWaiverAccepted(!!c)} className="mt-0.5" data-testid="u-waiver-accept" />
                <span><strong>Liability waiver (required):</strong> I have read and agree to the liability waiver above, and I am the parent or legal guardian of the child named above. *</span>
              </label>
            </Section>

            {/* Signature */}
            <Section icon={PenTool} title="Signature" accent={accent} testid="section-signature">
              <Label className="text-xs">Type your full name to sign *</Label>
              <Input value={signature} onChange={(e) => setSignature(e.target.value)} placeholder="e.g. Jane Doe"
                className="rounded-xl border-[#E8E5DF] mt-1 font-[Fredoka] text-lg" data-testid="u-signature" />
              <p className="text-[11px] text-[#6B6B7A]">Signed on {new Date().toLocaleDateString()} · this is your legal electronic signature.</p>
            </Section>
          </motion.div>
        )}
      </AnimatePresence>

      {response && (
        <Button onClick={submit} disabled={submitting || (response === 'yes' && !canSubmitYes)}
          className="w-full rounded-full text-white py-6 text-base font-semibold mt-6"
          style={{ background: accent }} data-testid="unified-submit">
          {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin inline" /> Submitting…</>
            : response === 'yes' ? 'Submit RSVP, consent & waiver' : 'Submit RSVP'}
        </Button>
      )}
    </div>
  );
};

export default UnifiedRsvpForm;
